import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';

Vue.use(Vuex);

export default new Vuex.Store({
    plugins: [createPersistedState()],
    state: {
        isSigningIn: false,
        isExitingDemo: false,
        isAuthenticated: false,
        hasConnectedPlatform: false,
        hasConfiguredProjectSpace: false,
        hasConfiguredProject: false,
        configuredProjectSpaces: [],
        configuredProjects: [],
        selectedProjectSpaceId: null,
        selectedProjectId: null,
        hasBacklogSetup: false,
        hasRepoSetup: false,
        // Deprecated aliases kept for backward compatibility with persisted state
        hasToken: false,
        hasConfig: false,
        account: {},
        lastPickedSprint: null,
        employeeRole: null,
        lastPickedCalendarDate: null,
        lastSwitchedShowRemovedWorkItems: false,
        lastTaskStateFilter: ["New", "To Do", "In Progress", "Done"],
        lastSwitchedShowAnyAssignedWorkItems: false,
        lastToggleAllCollapsesAreOpen: true,
        lastPrGroupMode: "repo",
        lastPrStatusFilter: ["Active", "Draft", "Completed"],
        lastPrShowOptionalReviewers: false,
        lastPrShowUnassigned: false,
        pendingTimeItemsByDate: {},
        timeItemClientAddedAt: {},
        workspace: null,
        workspaces: [],
        pendingTeamInvitations: [],
        activeTeamNudges: [],
        theme: 'dark',
        countryCode: 'BG',
        publicHolidays: {},
        locale: 'en-US',
        timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat) ? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC') : 'UTC',
        isBetaUser: false,
        hasAcknowledgedBetaWelcome: false,
        sprints: [],
        lastSprintsProjectId: null,
        lastSprintsPage: 1,
        lastSprintsTotalPages: 1,
        teamMembersByProject: {},
        allIdentitiesByProject: {},
        isServiceUnavailable: false,
        isBetaWelcomeOpen: false,
        devSimulator: {
            envOverride: (typeof localStorage !== 'undefined' ? localStorage.getItem('velocytrack_dev_sim_env') : null) || null
        }
    },
    getters: {
        isPublicHoliday: (state) => (ymd, country) => {
            if (!ymd) return false;
            const year = ymd.slice(0, 4);
            const cCode = country || state.countryCode || 'BG';
            const key = `${cCode}_${year}`;
            const list = state.publicHolidays?.[key];
            if (Array.isArray(list)) {
                return list.some(h => h.date === ymd);
            }
            return false;
        }
    },
    mutations: {
        setPublicHolidays(state, { key, holidays }) {
            Vue.set(state.publicHolidays, key, holidays || []);
        },
        setBetaUser(state, isBeta) {
            state.isBetaUser = !!isBeta;
        },
        setAcknowledgedBetaWelcome(state, val) {
            state.hasAcknowledgedBetaWelcome = !!val;
        },
        setBetaWelcomeOpen(state, val) {
            state.isBetaWelcomeOpen = !!val;
        },
        setServiceUnavailable(state, val) {
            state.isServiceUnavailable = !!val;
        },
        setDevSimulatorEnv(state, env) {
            if (!state.devSimulator) {
                Vue.set(state, 'devSimulator', { envOverride: null });
            }
            state.devSimulator.envOverride = env || null;
            try {
                if (env) {
                    localStorage.setItem('velocytrack_dev_sim_env', env);
                } else {
                    localStorage.removeItem('velocytrack_dev_sim_env');
                }
            } catch (e) {
                // ignore storage errors
            }
        },
        resetDevSimulator(state) {
            Vue.set(state, 'devSimulator', {
                envOverride: null
            });
            try {
                localStorage.removeItem('velocytrack_dev_sim_env');
            } catch (e) {
                // ignore
            }
        },
        resetTransientModeState(state) {
            state.selectedProjectId = null;
            state.lastPickedSprint = null;
            state.lastPickedCalendarDate = null;
            state.pendingTimeItemsByDate = {};
            state.timeItemClientAddedAt = {};
            state.lastTaskStateFilter = ["New", "To Do", "In Progress", "Done"];
            state.lastSwitchedShowRemovedWorkItems = false;
            state.lastSwitchedShowAnyAssignedWorkItems = false;
            state.lastToggleAllCollapsesAreOpen = true;
            state.lastPrGroupMode = "repo";
            state.lastPrStatusFilter = ["Active", "Draft", "Completed"];
            state.lastPrShowOptionalReviewers = false;
            state.lastPrShowUnassigned = false;
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
            state.teamMembersByProject = {};
            state.allIdentitiesByProject = {};
            state.isServiceUnavailable = false;
        },
        setTheme(state, theme) {
            state.theme = theme;
        },
        setCountryCode(state, code) {
            state.countryCode = code;
        },
        setLocale(state, locale) {
            state.locale = locale;
        },
        setTimezone(state, tz) {
            state.timezone = tz;
        },
        setAccount(state, account) {
            if (account) {
                state.account = account;
                state.isAuthenticated = true;
                if (account.isDemoAccount || (typeof account.email === 'string' && account.email.includes('@demo'))) {
                    state.theme = 'dark';
                }
            }
        },
        setWorkspace(state, workspace) {
            state.workspace = workspace;
        },
        setDailyMutationsRemaining(state, payload) {
            const remaining = typeof payload === 'number' ? payload : (payload?.remaining ?? 0);
            const limit = (typeof payload === 'object' && payload?.limit) ? payload.limit : (state.workspace?.dailyMutationLimit ?? 5);
            const used = Math.max(0, limit - remaining);
            if (state.workspace) {
                Vue.set(state.workspace, 'dailyMutationLimit', limit);
                Vue.set(state.workspace, 'dailyMutationsRemaining', remaining);
                Vue.set(state.workspace, 'dailyMutationsUsed', used);
                Vue.set(state.workspace, 'isViewOnly', remaining <= 0);
                Vue.set(state.workspace, 'isPersonalViewOnly', remaining <= 0);
            }
            const updateSpace = (p) => {
                if (p && !p.isSharedProjectSpace && !p.sharedWorkspaceId) {
                    Vue.set(p, 'dailyMutationLimit', limit);
                    Vue.set(p, 'dailyMutationsRemaining', remaining);
                    Vue.set(p, 'dailyMutationsUsed', used);
                    Vue.set(p, 'isViewOnly', remaining <= 0);
                }
            };
            (state.configuredProjectSpaces || []).forEach(updateSpace);
            (state.configuredProjects || []).forEach(updateSpace);
        },
        setWorkspaces(state, workspaces) {
            state.workspaces = Array.isArray(workspaces) ? workspaces : [];
        },
        setPendingTeamInvitations(state, invitations) {
            state.pendingTeamInvitations = Array.isArray(invitations) ? invitations : [];
        },
        setActiveTeamNudges(state, nudges) {
            state.activeTeamNudges = Array.isArray(nudges) ? nudges : [];
        },
        signOut(state) {
            state.account = {};
            state.isAuthenticated = false;
            state.hasConnectedPlatform = false;
            state.hasConfiguredProjectSpace = false;
            state.hasConfiguredProject = false;
            state.hasToken = false;
            state.hasConfig = false;
            state.configuredProjectSpaces = [];
            state.configuredProjects = [];
            state.selectedProjectSpaceId = null;
            state.selectedProjectId = null;
            state.hasBacklogSetup = false;
            state.hasRepoSetup = false;
            state.employeeRole = null;
            state.workspace = null;
            state.workspaces = [];
            state.pendingTeamInvitations = [];
            state.activeTeamNudges = [];
            state.isBetaUser = false;
            state.hasAcknowledgedBetaWelcome = false;
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
            state.allIdentitiesByProject = {};
            state.dismissedDiscussions = {};
            Vue.set(state, 'devSimulator', {
                envOverride: null
            });
            try {
                localStorage.removeItem('velocytrack_dev_sim_env');
            } catch (e) {
                // ignore
            }
        },
        setConfiguredProjectSpaces(state, spaces) {
            const list = (spaces || []).map(p => (p && typeof p === 'object' ? { employeeRoleOverride: null, ...p } : p));
            state.configuredProjectSpaces = list;
            state.configuredProjects = list;
            state.hasBacklogSetup = list.some(p => !p.isArchived && !!p.backlogLinkId && !!p.backlogProjectId);
            state.hasRepoSetup = list.some(p => !p.isArchived && !!p.repoLinkId && (!!p.repoProjectId || !!p.repoProjectName));
        },
        setConfiguredProjects(state, projects) {
            const list = (projects || []).map(p => (p && typeof p === 'object' ? { employeeRoleOverride: null, ...p } : p));
            state.configuredProjectSpaces = list;
            state.configuredProjects = list;
            state.hasBacklogSetup = list.some(p => !p.isArchived && !!p.backlogLinkId && !!p.backlogProjectId);
            state.hasRepoSetup = list.some(p => !p.isArchived && !!p.repoLinkId && (!!p.repoProjectId || !!p.repoProjectName));
        },
        setSelectedProjectSpaceId(state, id) {
            if (state.selectedProjectSpaceId !== id) {
                state.sprints = [];
                state.lastSprintsProjectId = null;
                state.lastSprintsPage = 1;
                state.lastSprintsTotalPages = 1;
            }
            state.selectedProjectSpaceId = id;
            state.selectedProjectId = id;
        },
        setSelectedProjectId(state, id) {
            if (state.selectedProjectId !== id) {
                state.sprints = [];
                state.lastSprintsProjectId = null;
                state.lastSprintsPage = 1;
                state.lastSprintsTotalPages = 1;
            }
            state.selectedProjectSpaceId = id;
            state.selectedProjectId = id;
        },
        clearSelectedProjectSpaceId(state) {
            state.selectedProjectSpaceId = null;
            state.selectedProjectId = null;
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
        },
        clearSelectedProjectId(state) {
            state.selectedProjectSpaceId = null;
            state.selectedProjectId = null;
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
        },
        hasConnectedPlatform(state, hasIt) {
            state.hasConnectedPlatform = hasIt;
            state.hasToken = hasIt;
        },
        hasConfiguredProjectSpace(state, hasIt) {
            state.hasConfiguredProjectSpace = hasIt;
            state.hasConfiguredProject = hasIt;
            state.hasConfig = hasIt;
        },
        hasConfiguredProject(state, hasIt) {
            state.hasConfiguredProjectSpace = hasIt;
            state.hasConfiguredProject = hasIt;
            state.hasConfig = hasIt;
        },
        // Deprecated aliases kept for backward compatibility
        hasToken(state, hasIt) {
            state.hasConnectedPlatform = hasIt;
            state.hasToken = hasIt;
        },
        hasConfig(state, hasIt) {
            state.hasConfiguredProject = hasIt;
            state.hasConfig = hasIt;
        },
        setSigningIn(state, isOn) {
            state.isSigningIn = isOn;
        },
        setIsExitingDemo(state, isExiting) {
            state.isExitingDemo = !!isExiting;
        },
        setSprintPick(state, sprint) {
            state.lastPickedSprint = sprint;
        },
        setEmployeeRoleId(state, role) {
            state.employeeRole = role
        },
        setLastPickedCalendarDate(state, date) {
            state.lastPickedCalendarDate = date;
        },
        setLastSwitchedShowRemovedWorkItems(state, isSwitched) {
            state.lastSwitchedShowRemovedWorkItems = isSwitched;
        },
        setLastTaskStateFilter(state, filter) {
            state.lastTaskStateFilter = filter;
        },
        setLastSwitchedShowAnyAssignedWorkItems(state, isSwitched) {
            state.lastSwitchedShowAnyAssignedWorkItems = isSwitched;
        },
        setLastToggleAllCollapsesAreOpen(state, isToggle) {
            state.lastToggleAllCollapsesAreOpen = isToggle;
        },
        setLastPrGroupMode(state, groupMode) {
            state.lastPrGroupMode = groupMode;
        },
        setLastPrStatusFilter(state, statusFilter) {
            state.lastPrStatusFilter = statusFilter;
        },
        setLastPrShowOptionalReviewers(state, showOptional) {
            state.lastPrShowOptionalReviewers = showOptional;
        },
        setLastPrShowUnassigned(state, showUnassigned) {
            state.lastPrShowUnassigned = showUnassigned;
        },
        setPendingTimeItemsForDate(state, { date, items }) {
            if (!date)
                return;
            if (!items || !items.length) {
                Vue.delete(state.pendingTimeItemsByDate, date);
                return;
            }
            Vue.set(state.pendingTimeItemsByDate, date, items);
        },
        setTimeItemClientAddedAt(state, { vid, addedAt }) {
            if (!vid)
                return;
            Vue.set(state.timeItemClientAddedAt, vid, addedAt);
        },
        setSprints(state, payload) {
            state.sprints = payload.sprints || [];
            state.lastSprintsProjectId = payload.projectId || null;
            state.lastSprintsPage = payload.page || 1;
            state.lastSprintsTotalPages = payload.totalPages || 1;
        },
        setTeamMembers(state, { projectId, members }) {
            const key = projectId || 'default';
            Vue.set(state.teamMembersByProject, key, members || []);
        },
        clearTeamMembers(state) {
            state.teamMembersByProject = {};
        },
        setIdentities(state, { projectId, identities }) {
            const key = projectId || 'default';
            if (!state.allIdentitiesByProject) {
                Vue.set(state, 'allIdentitiesByProject', {});
            }
            Vue.set(state.allIdentitiesByProject, key, identities || []);
        },
        clearIdentities(state) {
            state.allIdentitiesByProject = {};
        },
        dismissDiscussion(state, { workItemId, lastCommentId = 0 }) {
            if (!state.dismissedDiscussions) {
                Vue.set(state, 'dismissedDiscussions', {});
            }
            Vue.set(state.dismissedDiscussions, workItemId, {
                dismissedAt: new Date().toISOString(),
                lastCommentId
            });
        },
        undismissDiscussion(state, workItemId) {
            if (state.dismissedDiscussions && state.dismissedDiscussions[workItemId]) {
                Vue.delete(state.dismissedDiscussions, workItemId);
            }
        },
        clearSprints(state) {
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
        },
        clearSprintState(state, projectId = null) {
            state.sprints = [];
            state.lastSprintsProjectId = null;
            state.lastSprintsPage = 1;
            state.lastSprintsTotalPages = 1;
            state.lastPickedSprint = null;
            if (projectId) {
                Vue.delete(state.teamMembersByProject, projectId);
                Vue.delete(state.allIdentitiesByProject, projectId);
            } else {
                state.teamMembersByProject = {};
                state.allIdentitiesByProject = {};
            }
        }
    },
    actions: {
        invalidateSprintState({ commit }, projectId = null) {
            commit('clearSprintState', projectId);
            try {
                localStorage.removeItem('velocytrack_last_sprint');
                if (projectId) {
                    localStorage.removeItem('velocytrack_last_sprint_' + projectId);
                }
                for (let i = localStorage.length - 1; i >= 0; i--) {
                    const k = localStorage.key(i);
                    if (k && k.startsWith('velocytrack_last_sprint')) {
                        localStorage.removeItem(k);
                    }
                }
            } catch (e) {
                // ignore
            }
        },
        async fetchSprintsAsync({ commit, state }, { projectId, page = null, sprintPath = null, force = false } = {}) {
            const http = Vue.prototype.$http;
            if (!http) {
                return { sprints: state.sprints, page: state.lastSprintsPage, totalPages: state.lastSprintsTotalPages };
            }

            const targetPage = page !== null ? page : null;
            const sameProject = state.lastSprintsProjectId && state.lastSprintsProjectId === projectId;
            const samePage = targetPage === null || targetPage === state.lastSprintsPage;

            if (!force && sameProject && samePage && state.sprints && state.sprints.length > 0) {
                return {
                    sprints: state.sprints,
                    page: state.lastSprintsPage,
                    totalPages: state.lastSprintsTotalPages
                };
            }

            const params = {};
            if (projectId) {
                params.projectId = projectId;
            }
            if (targetPage !== null) {
                params.page = targetPage;
            } else if (sprintPath) {
                params.sprintPath = sprintPath;
            }
            if (force) {
                params.force = true;
            }

            let resp = await http.get('/Azure/ListSprints', { params });
            let data = resp.data;

            if (params.sprintPath && (!data || !data.sprints || data.sprints.length === 0)) {
                delete params.sprintPath;
                resp = await http.get('/Azure/ListSprints', { params });
                data = resp.data;
            }

            let loadedSprints = [];
            let loadedPage = 1;
            let loadedTotalPages = 1;
            let loadedIsAllTimeLocked = Boolean(data?.isAllTimeLocked);

            if (data && data.sprints) {
                loadedSprints = data.sprints;
                loadedPage = data.page || 1;
                loadedTotalPages = data.totalPages || 1;
            } else if (Array.isArray(data)) {
                loadedSprints = data;
                loadedPage = 1;
                loadedTotalPages = 1;
            }

            commit('setSprints', {
                sprints: loadedSprints,
                projectId: projectId,
                page: loadedPage,
                totalPages: loadedTotalPages
            });

            return {
                sprints: loadedSprints,
                page: loadedPage,
                totalPages: loadedTotalPages,
                isAllTimeLocked: loadedIsAllTimeLocked
            };
        },
        async fetchTeamMembersAsync({ commit, state }, { projectId, force = false } = {}) {
            const key = projectId || 'default';
            if (!force && state.teamMembersByProject && state.teamMembersByProject[key] && state.teamMembersByProject[key].length > 0) {
                return state.teamMembersByProject[key];
            }
            const http = Vue.prototype.$http;
            if (!http) {
                return [];
            }
            const params = {};
            if (projectId) {
                params.projectId = projectId;
            }
            let members = [];
            try {
                const resp = await http.get('/Azure/ListTeamMembers', { params });
                members = Array.isArray(resp.data) ? [...resp.data] : [];
            } catch (_) {
                members = [];
            }

            // Also merge VelocyTrack Squad members if the project space belongs to a shared Team
            try {
                const configuredProjects = state.configuredProjectSpaces || state.configuredProjects || [];
                const proj = projectId
                    ? configuredProjects.find(p => p.id === projectId || p.id === parseInt(projectId, 10))
                    : (configuredProjects.find(p => p.isPrimary) || configuredProjects[0]);
                const wsId = proj?.sharedWorkspaceId || state.workspace?.workspaceId;
                if (wsId) {
                    const wsResp = await http.get('/Workspace/Members', { params: { workspaceId: wsId } });
                    const wsMembers = Array.isArray(wsResp.data) ? wsResp.data : [];
                    const currentEmail = (state.account?.email || '').toLowerCase();
                    for (const wm of wsMembers) {
                        const uniqueKey = (wm.externalIdentity || wm.email || '').trim();
                        if (!uniqueKey) continue;
                        const exists = members.some(m =>
                            (m.uniqueName || '').toLowerCase() === uniqueKey.toLowerCase() ||
                            (m.displayName || '').toLowerCase() === (wm.fullName || '').toLowerCase()
                        );
                        if (!exists) {
                            members.push({
                                id: wm.accountUid,
                                displayName: wm.fullName || wm.email,
                                uniqueName: uniqueKey,
                                imageUrl: wm.picture || '',
                                isCurrentUser: (wm.email || '').toLowerCase() === currentEmail
                            });
                        }
                    }
                }
            } catch (_) {
                // Ignore if user has no team workspace
            }

            commit('setTeamMembers', { projectId, members });
            return members;
        },
        async searchIdentitiesAsync({ commit, state }, { query = '', projectId, force = false } = {}) {
            const key = projectId || 'default';
            const trimmed = (query || '').trim();
            if (!force && !trimmed && state.allIdentitiesByProject && state.allIdentitiesByProject[key] && state.allIdentitiesByProject[key].length > 0) {
                return state.allIdentitiesByProject[key];
            }
            const http = Vue.prototype.$http;
            if (!http) {
                return [];
            }
            const params = {};
            if (projectId) {
                params.projectId = projectId;
            }
            if (trimmed) {
                params.query = trimmed;
            }
            try {
                const resp = await http.get('/Azure/SearchIdentities', { params });
                const list = resp.data || [];
                if (!trimmed) {
                    commit('setIdentities', { projectId, identities: list });
                }
                return list;
            } catch (err) {
                console.error('Failed to search identities:', err);
                return [];
            }
        },
        async fetchPublicHolidaysAsync({ commit, state }, { country, year, force = false } = {}) {
            const cCode = country || state.countryCode || 'BG';
            const yr = year || new Date().getFullYear();
            const key = `${cCode}_${yr}`;
            if (!force && state.publicHolidays && state.publicHolidays[key] && state.publicHolidays[key].length > 0) {
                return state.publicHolidays[key];
            }
            const http = Vue.prototype.$http;
            if (!http) {
                return [];
            }
            try {
                const resp = await http.get(`/ExternalApi/PublicHolidays/${yr}?countryCode=${cCode}`);
                const holidays = Array.isArray(resp?.data) ? resp.data : [];
                commit('setPublicHolidays', { key, holidays });
                return holidays;
            } catch (err) {
                console.error('Failed to fetch public holidays:', err);
                return [];
            }
        }
    }
});

